import { personScopeUrl } from "../components/personScope";
import { formatPersonName } from "../components/personName";
import { formatStreckeQuantity } from "../components/streckeMappings";
import React, { useState, useEffect } from "react";
import api from "../api/axios";
import { Table, TableHead, TableRow, TableCell, TableBody, TableContainer, 
  Paper, Button, Box, Typography, IconButton, Collapse, Snackbar, Alert, Tooltip } from "@mui/material";
import Edit from "@mui/icons-material/Edit";
import ShoppingCart from "@mui/icons-material/ShoppingCart";
import Add from "@mui/icons-material/Add";
import Delete from "@mui/icons-material/Delete";
import ContentCopy from "@mui/icons-material/ContentCopy";
import StreckeDialog from "../components/StreckeDialog";
import VerkaufDialog from "../components/VerkaufDialog";
import MehrfachVerkaufDialog from "../components/MehrfachVerkaufDialog";
import PageHeader from "../components/PageHeader";
import LoadingIndicator from '../components/LoadingIndicator';
import LoadError from '../components/LoadError';
import { getStreckeAltersklasse, mapVerkaufToDialogRow } from "../components/verkaufMappings";
import { persistVerkaufChanges } from "../components/verkaufPersistence";
import { mapStreckeToCreateValues } from "../components/streckeMappings";
import { useBusinessContext, businessError, mandantUrl } from "../BusinessContext";
import dayjs from "dayjs";
import { useLocation } from "react-router-dom"; // <--- NEU
import RecordLink from "../components/RecordLink";
import { useSelectedRecord, useTargetList } from "../components/useRecordTarget";
import API_BASE_URL from "../config"; // <--- NEU

function useQuery() {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}

// Keep the tenant catalog mounted while the scoped records retain their own
// business/year lifecycle (including all existing sales dialogs and callbacks).
function Strecke() {
  const { mandantId, ready, scopeKey, loading, error } = useBusinessContext();
  const [catalog, setCatalog] = useState({ mandantId: null, rows: [], loading: true, error: "" });
  const [catalogAttempt, setCatalogAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    setCatalog({ mandantId, rows: [], loading: true, error: "" });
    if (mandantId) {
      api.get(mandantUrl(`${API_BASE_URL}/wild`, mandantId))
        .then(response => {
          if (active) setCatalog({ mandantId, rows: Array.isArray(response.data) ? response.data : [], loading: false, error: "" });
        })
        .catch(fetchError => {
          if (active) setCatalog({ mandantId, rows: [], loading: false, error: businessError(fetchError, "Wilddaten konnten nicht geladen werden.") });
        });
    }
    return () => { active = false; };
  }, [mandantId, catalogAttempt]);
  const current = catalog.mandantId === mandantId;
  if (!ready) return loading ? <LoadingIndicator label="Geschäftskontext wird geladen …" /> : <Alert severity={error ? "error" : "info"}>{error || "Bitte einen vollständigen Geschäftskontext auswählen."}</Alert>;
  return <StreckeRecords key={scopeKey} wildDaten={current ? catalog.rows : []}
    wildLoading={!current || catalog.loading} wildError={current ? catalog.error : ""} retryWild={() => setCatalogAttempt(value => value + 1)} />;
}

function StreckeRecords({ wildDaten, wildLoading, wildError, retryWild }) {
  const [strecken, setStrecken] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editStrecke, setEditStrecke] = useState(null);
  const [dialogInitialValues, setDialogInitialValues] = useState(null);
  const [verkaufDialogOpen, setVerkaufDialogOpen] = useState(false);
  const [mehrfachVerkaufOpen, setMehrfachVerkaufOpen] = useState(false);
  const [verkaufStreckeId, setVerkaufStreckeId] = useState(null);
  const [kundenListe, setKundenListe] = useState([]);
  const [jaegerListe, setJaegerListe] = useState([]);
  const [peopleLoading, setPeopleLoading] = useState(false), [peopleError, setPeopleError] = useState(''), [peopleAttempt, setPeopleAttempt] = useState(0);
  const [einrichtungen, setEinrichtungen] = useState([]);
  const [einrichtungenLoading, setEinrichtungenLoading] = useState(false), [einrichtungenError, setEinrichtungenError] = useState('');
  const [einrichtungenAttempt, setEinrichtungenAttempt] = useState(0);
  const [expandedRow, setExpandedRow] = useState(null);
  const [verkaeufe, setVerkaeufe] = useState({});
  const [saleStatus, setSaleStatus] = useState({});
  const [activeStrecke, setActiveStrecke] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const { mandantId, betriebId, betriebsjahrId, betriebsjahre, params, withContext, selectedMandant } = useBusinessContext();

  const query = useQuery();

  const streckeUrl = `${API_BASE_URL}/strecke`;
  const verkaufUrl = `${API_BASE_URL}/verkauf`;
  const personenUrl = `${API_BASE_URL}/personen`;

  const { notice, loading, retry } = useTargetList(streckeUrl, "Id", setStrecken);
  const { selected, targetRef } = useSelectedRecord(strecken, "Id", setActiveStrecke);

  useEffect(() => {
    let active = true;
    setKundenListe([]); setJaegerListe([]);
    setPeopleLoading(Boolean(mandantId)); setPeopleError('');
    if (!mandantId) return;
    Promise.allSettled([
      api.get(personScopeUrl(`${personenUrl}?filter=kunden`, mandantId, betriebId)),
      api.get(personScopeUrl(`${personenUrl}?filter=jaeger&aktiv=true`, mandantId, betriebId)),
    ]).then(([customers, hunters]) => {
      if (!active) return;
      if (customers.status === 'fulfilled') setKundenListe(customers.value.data);
      if (hunters.status === 'fulfilled') setJaegerListe(hunters.value.data);
      if (customers.status === 'rejected' || hunters.status === 'rejected') setPeopleError('Kunden oder Jäger konnten nicht geladen werden.');
      setPeopleLoading(false);
    });
    return () => { active = false; };
  }, [personenUrl, mandantId, betriebId, peopleAttempt]);

  useEffect(() => {
    let active = true;
    setEinrichtungen([]); setEinrichtungenError(''); setEinrichtungenLoading(Boolean(mandantId && betriebId));
    if (!mandantId || !betriebId) return;
    const url = `${mandantUrl(`${API_BASE_URL}/einrichtungen`, mandantId)}&betrieb_id=${encodeURIComponent(betriebId)}`;
    api.get(url).then(response => {
      if (!active) return;
      setEinrichtungen(Array.isArray(response.data) ? response.data : []);
      setEinrichtungenLoading(false);
    }).catch(fetchError => {
      if (!active) return;
      setEinrichtungenError(businessError(fetchError, 'Einrichtungen konnten nicht geladen werden.'));
      setEinrichtungenLoading(false);
    });
    return () => { active = false; };
  }, [mandantId, betriebId, einrichtungenAttempt]);

  // Verkäufe für eine Strecke laden
  const loadVerkaeufe = (streckeId) => {
    setSaleStatus(previous => ({ ...previous, [streckeId]: { loading: true } }));
    api.get(withContext(`${verkaufUrl}?strecke_id=${streckeId}`))
      .then(res => {
        setVerkaeufe(prev => ({ ...prev, [streckeId]: res.data }));
        setSaleStatus(previous => ({ ...previous, [streckeId]: {} }));
        if (res.data.length === 0) setExpandedRow(prev => prev === streckeId ? null : prev);
      })
      .catch(() => { setSaleStatus(previous => ({ ...previous, [streckeId]: { error: 'Verkäufe konnten nicht geladen werden.' } })); });
  };

  const hasVerkaeufe = (eintrag) => verkaeufe[eintrag.Id] !== undefined
    ? verkaeufe[eintrag.Id].length > 0
    : Boolean(Number(eintrag.HasVerkaeufe));

  // Plus/Minus-Button-Handler
  const handleExpandClick = (streckeId) => {
    if (!hasVerkaeufe(strecken.find(eintrag => eintrag.Id === streckeId))) return;
    if (expandedRow === streckeId) {
      setExpandedRow(null);
    } else {
      setExpandedRow(streckeId);
      if (!verkaeufe[streckeId]) {
        loadVerkaeufe(streckeId);
      }
    }
  };

  // --- NEU: Selektierung und Aufklappen per Query-Parameter ---
  useEffect(() => {
    const selectId = query.get("select");
    if (selectId && strecken.length > 0) {
      const strecke = strecken.find(s => String(s.Id) === String(selectId));
      if (strecke) {
        setActiveStrecke(strecke);
        setExpandedRow(hasVerkaeufe(strecke) ? strecke.Id : null);
        if (hasVerkaeufe(strecke) && !verkaeufe[strecke.Id]) {
          loadVerkaeufe(strecke.Id);
        }
        // Optional: Nach dem Selektieren Query-Parameter entfernen
        // navigate("/strecke", { replace: true });
      }
    }
    // eslint-disable-next-line
  }, [strecken, query]);

  // Verkaufsdialog speichern
  const handleSaveVerkauf = (verkaufData) => {
    api.post(`${verkaufUrl}`, { ...verkaufData, ...params })
      .then(() => {
        if (verkaufData.strecke_id) loadVerkaeufe(verkaufData.strecke_id);
        setVerkaufDialogOpen(false);
      })
      .catch(err => { console.error("Fehler beim Speichern des Verkaufs:", err); setSnackbar({ open: true, message: businessError(err, "Fehler beim Speichern des Verkaufs"), severity: "error" }); });
  };

  // Streckendialog speichern
  const handleSaveStrecke = async (data) => {
    const { betriebsjahr_id: targetYear, ...fields } = data;
    const streckeData = { ...fields, ...params, betriebsjahr_id: targetYear ?? betriebsjahrId };
    try {
      if (editStrecke) {
        const target = Number(targetYear) === Number(betriebsjahrId)
          ? withContext(`${streckeUrl}/${editStrecke.Id}`) : `${streckeUrl}/${editStrecke.Id}`;
        await api.put(target, streckeData);
      } else await api.post(streckeUrl, streckeData);
      const response = await api.get(withContext(streckeUrl));
      setStrecken(response.data);
      setSnackbar({ open: true, message: "Streckeneintrag gespeichert.", severity: "success" });
    } catch (error) {
      setSnackbar({ open: true, message: businessError(error, "Strecke konnte nicht gespeichert werden."), severity: "error" });
      throw error;
    }
  };

  const handleCloseStreckeDialog = () => {
    setDialogOpen(false);
    setEditStrecke(null);
    setDialogInitialValues(null);
  };

  return (
    <Box sx={{ display: "flex", gap: 3 }}>
      <Box sx={{ flex: 1 }}>
        {/* Einheitliche Titelzeile mit direkt vorangestellter Aktion */}
        <PageHeader
          title="Strecke"
          action={<Button
            variant="contained"
            size="small"
            startIcon={<Add />}
            onClick={() => {
              setEditStrecke(null);
              setDialogInitialValues(null);
              setDialogOpen(true);
            }}
          >
            Neu
          </Button>}
        />
        {loading && <LoadingIndicator label="Strecke wird geladen …" />}
        {wildLoading && <LoadingIndicator label="Wilddaten werden geladen …" compact />}
        {peopleLoading && <LoadingIndicator label="Kunden und Jäger werden geladen …" compact />}
        {peopleError && <LoadError onRetry={() => setPeopleAttempt(value => value + 1)}>{peopleError}</LoadError>}
        {einrichtungenLoading && <LoadingIndicator label="Einrichtungen werden geladen …" compact />}
        {einrichtungenError && <LoadError onRetry={() => setEinrichtungenAttempt(value => value + 1)}>{einrichtungenError}</LoadError>}
        {notice && (notice.severity === 'error' ? <LoadError onRetry={retry}>{notice.text}</LoadError> : <Alert severity={notice.severity}>{notice.text}</Alert>)}
        {wildError && <LoadError onRetry={retryWild}>{wildError}</LoadError>}
        {Object.values(saleStatus).some(status => status.loading) && <LoadingIndicator label="Verkäufe werden geladen …" compact />}
        {Object.entries(saleStatus).filter(([, status]) => status.error).map(([id, status]) => <LoadError key={id} onRetry={() => loadVerkaeufe(Number(id))}>{status.error}</LoadError>)}
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Datum</TableCell>
                <TableCell>Wildname</TableCell>
                <TableCell>Altersklasse</TableCell>
                <TableCell>Jäger</TableCell>
                <TableCell>Menge</TableCell>
                <TableCell>Ort</TableCell>
                <TableCell>Einrichtung</TableCell>
                <TableCell>Bemerkung</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {strecken.map((eintrag) => (
                <React.Fragment key={eintrag.Id}>
                  <TableRow
                    hover
                    ref={String(eintrag.Id) === selected ? targetRef : undefined}
                    selected={expandedRow === eintrag.Id || (activeStrecke && activeStrecke.Id === eintrag.Id)}
                    onClick={() => setActiveStrecke(eintrag)}
                    sx={{
                      "& td": { whiteSpace: "nowrap" }
                    }}
                  >
                    <TableCell sx={{ width: 40 }}>
                      <Tooltip title={!hasVerkaeufe(eintrag) ? "Keine Verkäufe vorhanden" : expandedRow === eintrag.Id ? "Verkäufe ausblenden" : "Verkäufe anzeigen"}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={!hasVerkaeufe(eintrag)}
                            sx={{ "&.Mui-disabled": { color: "grey.300" } }}
                            color={expandedRow === eintrag.Id ? "primary" : "default"}
                            aria-label={!hasVerkaeufe(eintrag) ? "Keine Verkäufe vorhanden" : expandedRow === eintrag.Id ? "Verkäufe ausblenden" : "Verkäufe anzeigen"}
                            aria-expanded={hasVerkaeufe(eintrag) && expandedRow === eintrag.Id}
                            onClick={() => handleExpandClick(eintrag.Id)}
                          >
                            <ShoppingCart fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{eintrag.Datum ? dayjs(eintrag.Datum).format("DD.MM.YYYY") : ""}</TableCell>
                    <TableCell>{eintrag.Wildname}</TableCell>
                    <TableCell>{eintrag.Altersklasse}</TableCell>
                    <TableCell><RecordLink page="jaeger" id={eintrag.JaegerId}>{eintrag.NameNV}</RecordLink></TableCell>
                    <TableCell align="right">{formatStreckeQuantity(eintrag)}</TableCell>
                    <TableCell>{eintrag.Ort}</TableCell>
                    <TableCell>{eintrag.EinrichtungName || ""}</TableCell>
                    <TableCell>{eintrag.Bemerkung}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <IconButton
                        aria-label="Streckeneintrag bearbeiten"
                        color="primary"
                        onClick={e => {
                          e.stopPropagation();
                          setEditStrecke(eintrag);
                          setDialogInitialValues(eintrag);
                          setDialogOpen(true);
                        }}
                        size="small"
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <Tooltip title="Strecke kopieren">
                        <IconButton
                          aria-label="Strecke kopieren"
                          color="primary"
                          onClick={e => {
                            e.stopPropagation();
                            setEditStrecke(null);
                            setDialogInitialValues(mapStreckeToCreateValues(eintrag));
                            setDialogOpen(true);
                          }}
                          size="small"
                        >
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <IconButton
                        aria-label="Verkäufe verwalten"
                        color="secondary"
                        onClick={e => {
                          e.stopPropagation();
                          setVerkaufStreckeId(eintrag.Id); // <-- Strecke setzen!
                          if (!verkaeufe[eintrag.Id]) {
                            loadVerkaeufe(eintrag.Id);
                          }
                          setMehrfachVerkaufOpen(true);
                        }}
                        size="small"
                      >
                        <ShoppingCart fontSize="small" />
                      </IconButton>
                      <IconButton
                        aria-label="Streckeneintrag löschen"
                        color="error"
                        onClick={e => {
                          e.stopPropagation();
                          if (window.confirm("Soll dieser Streckeneintrag wirklich gelöscht werden?")) {
                            api.delete(withContext(`${streckeUrl}/${eintrag.Id}`))
                              .then(() => {
                                setSnackbar({ open: true, message: "Streckeneintrag gelöscht.", severity: "success" });
                                setStrecken(strecken.filter(s => s.Id !== eintrag.Id));
                              })
                              .catch((error) => {
                                const conflictMessage = error.response?.status === 409
                                  ? error.response?.data?.error
                                  : null;
                                setSnackbar({
                                  open: true,
                                  message: conflictMessage || "Fehler beim Löschen!",
                                  severity: "error"
                                });
                              });
                          }
                        }}
                        size="small"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  {/* Verkäufe-Tabelle */}
                  {hasVerkaeufe(eintrag) && <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
                      <Collapse in={expandedRow === eintrag.Id} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1, ml: 4, bgcolor: "#f5f5f5", borderRadius: 1, p: 2 }}>
                          <Typography variant="subtitle1" gutterBottom>
                            Verkäufe
                          </Typography>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Datum</TableCell>
                                <TableCell>Kunde</TableCell>
                                <TableCell>Menge</TableCell>
                                <TableCell>Preis</TableCell>
                                <TableCell>Betrag</TableCell>
                                <TableCell>Bezahlt</TableCell>
                                <TableCell>Bemerkung</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {(verkaeufe[eintrag.Id] || []).map((verkauf) => (
                                  <TableRow key={verkauf.v_id}>
                                    <TableCell><RecordLink page="verkauf" id={verkauf.v_id}>{verkauf.v_datum ? dayjs(verkauf.v_datum).format("DD.MM.YYYY") : ""}</RecordLink></TableCell>
                                    <TableCell><RecordLink page="kunden" id={verkauf.k_id}>{formatPersonName(verkauf.k_nachname, verkauf.k_vorname)}</RecordLink></TableCell>
                                    <TableCell>{verkauf.v_menge + ' ' + (verkauf.v_einheit || '')}</TableCell>
                                    <TableCell>{verkauf.v_preis}</TableCell>
                                    <TableCell>
                                      {verkauf.v_betrag}
                                      {verkauf.v_betrag !== undefined && verkauf.v_betrag !== null && verkauf.betrag !== "" ? " €" : ""}
                                    </TableCell>
                                    <TableCell>{verkauf.v_bezahlt ? "Ja" : "Nein"}</TableCell>
                                    <TableCell>{verkauf.v_bemerkung}</TableCell>
                                  </TableRow>
                                ))}
                            </TableBody>
                          </Table>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <StreckeDialog
          open={dialogOpen}
          onClose={handleCloseStreckeDialog}
          onSave={handleSaveStrecke}
          wildtier={dialogInitialValues}
          yearLabel={selectedMandant?.BetriebsjahrTitel}
          betriebsjahrId={betriebsjahrId}
          betriebsjahre={betriebsjahre}
          jaegerListe={jaegerListe}
          einrichtungen={einrichtungen}
          einrichtungenLoading={einrichtungenLoading}
          wildDaten={wildDaten}
          wildLoading={wildLoading}
        />
        <VerkaufDialog
          open={verkaufDialogOpen}
          onClose={() => setVerkaufDialogOpen(false)}
          onSave={handleSaveVerkauf}
          streckenListe={strecken}
          kundenListe={kundenListe}
          verkauf={verkaufStreckeId ? { strecke_id: verkaufStreckeId } : null}
        />
        <MehrfachVerkaufDialog
          loading={peopleLoading || Boolean(saleStatus[verkaufStreckeId]?.loading)}
          open={mehrfachVerkaufOpen}
          onClose={() => setMehrfachVerkaufOpen(false)}
          onSave={async ({ activeRows, deletedIds }) => {
            try {
              await persistVerkaufChanges({
                verkaufUrl,
                context: params,
                streckeId: verkaufStreckeId,
                activeRows,
                deletedIds
              });
              setSnackbar({ open: true, message: "Verkäufe erfolgreich gespeichert.", severity: "success" });
              if (verkaufStreckeId) loadVerkaeufe(verkaufStreckeId);
              setMehrfachVerkaufOpen(false);
            } catch (error) {
              const message = error.response?.data?.error || "Fehler beim Speichern der Verkäufe!";
              setSnackbar({ open: true, message, severity: "error" });
              if (verkaufStreckeId) loadVerkaeufe(verkaufStreckeId);
              throw error;
            }
          }}
          kundenListe={kundenListe}
          defaultDatum={(() => {
            const strecke = strecken.find(s => s.Id === verkaufStreckeId);
            return strecke?.Zerlegedatum?.trim() || strecke?.Datum || "";
          })()}
          wildDefaults={
            (() => {
              const strecke = strecken.find(s => s.Id === verkaufStreckeId);
              if (!strecke) return undefined;
              return {
                Std_Einheit: strecke.Std_Einheit,
                Std_Preis: strecke.Std_Preis
              };
            })()
          }
          // Übergib die bisherigen Verkäufe:
          initialVerkaeufe={(verkaeufe[verkaufStreckeId] || []).map(mapVerkaufToDialogRow)}
        />
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert 
            onClose={() => setSnackbar({ ...snackbar, open: false })} 
            severity={snackbar.severity} 
            sx={{ width: "100%" }}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
      <Box sx={{ minWidth: 300, maxWidth: 400, bgcolor: "#f5f5f5", p: 2, borderRadius: 2, height: "fit-content" }}>
        {activeStrecke ? (
          <>
            <Typography variant="h6" gutterBottom>Streckendetails</Typography>
            <Typography><b>Id:</b> {activeStrecke.Id}</Typography>
            <Typography><b>Datum:</b> {activeStrecke.Datum ? dayjs(activeStrecke.Datum).format("DD.MM.YYYY") : ""}</Typography>
            <Typography><b>Wildname:</b> {activeStrecke.Wildname}</Typography>
            <Typography><b>Altersklasse:</b> {getStreckeAltersklasse(activeStrecke)}</Typography>
            <Typography><b>Jäger:</b> {activeStrecke.NameNV}</Typography>
            <Typography><b>Alter:</b> {activeStrecke.Wildalter}</Typography>
            <Typography><b>Geschlecht:</b> {activeStrecke.Geschlecht}</Typography>
            <Typography><b>Menge:</b> {formatStreckeQuantity(activeStrecke)}</Typography>
            <Typography><b>Ort:</b> {activeStrecke.Ort}</Typography>
            <Typography><b>Bemerkung:</b> {activeStrecke.Bemerkung}</Typography>
            <Typography><b>Zerleger:</b> {activeStrecke.Zerleger}</Typography>
            <Typography><b>Zerlegedatum:</b> {activeStrecke.Zerlegedatum ? dayjs(activeStrecke.Zerlegedatum).format("DD.MM.YYYY") : ""}</Typography>
            <Typography><b>Zerlegekosten:</b> {activeStrecke.Zerlegekosten}</Typography>
          </>
        ) : (
          <Typography variant="body1" color="text.secondary">
            Bitte wählen Sie einen Eintrag aus der Liste aus.
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export default Strecke;
